import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  className?: string;
  disabled?: boolean;
}

export function CustomSelect({ value, onChange, options, className = '', disabled = false }: CustomSelectProps) {
  const { darkMode } = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 border rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${
          darkMode
            ? 'border-slate-600 bg-slate-700 text-white'
            : 'border-slate-300 bg-white text-slate-900'
        }`}
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${
          darkMode ? 'text-slate-400' : 'text-slate-500'
        }`} />
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 mt-1 rounded-lg border shadow-lg z-50 overflow-hidden ${
          darkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'
        }`}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2.5 text-left text-sm cursor-pointer transition-colors ${
                option === value
                  ? darkMode
                    ? 'bg-blue-600/30 text-blue-300'
                    : 'bg-blue-50 text-blue-700'
                  : darkMode
                    ? 'text-slate-200 hover:bg-slate-600'
                    : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
